import type { EventSlide } from '@/types/event';
import { getSize, getHSize } from '@/utils/size';
import useBaseSize from '@/hooks/useBaseSize';
import { useConfigStore } from '@/stores/configStore';
import { useTickSoundInteraction } from '@/hooks';
import { useTranslation } from 'react-i18next';
interface EventGridProps {
    slideItems: (EventSlide & { tabIndex?: number })[];
    onItemClick?: (category: string) => void;
}

export default function EventGrid({ slideItems, onItemClick }: EventGridProps) {
    const { baseWidth, baseHeight } = useBaseSize();
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const isFullScreen = screenMode === 'fullscreen';
    const { handleInteraction, handleKeyboardInteraction } = useTickSoundInteraction();
    const { t } = useTranslation();

    return (
        <div
            className="grid gap-[calc(24*var(--vw-unit))]"
            style={
                isFullScreen
                    ? {
                        gridTemplateColumns: `repeat(2, ${getSize(320, baseWidth)})`,
                        gridTemplateRows: `repeat(3, ${getHSize(370, baseHeight)})`,
                    }
                    : {
                        display: 'grid',
                        gridTemplateColumns: `repeat(2, ${getSize(320, baseWidth)})`,
                    }
            }
        >
            {slideItems.map((item) => {
                return (
                    <div
                        key={item.id}
                        data-type="button"
                        data-role={t('common.button.tts.button')}
                        tabIndex={item.tabIndex ?? 4}
                        aria-label={item.header + ' ' + item.description}
                        className={`contrast:border-[calc(1*var(--vh-unit))] contrast:border-white focus-border rounded-[calc(8*var(--vh-unit))] border-[calc(1*var(--vh-unit))] border-[var(--color-primary-black-light)]`}
                        onClick={(e) => handleInteraction(e, () => onItemClick?.(item.id))}
                        data-interactive
                        onKeyDown={(e) => handleKeyboardInteraction(e, () => onItemClick?.(item.id))}
                    >
                        <div className="h-[calc(272*var(--vh-unit))] overflow-hidden rounded-t-[calc(8*var(--vh-unit))]">
                            <img src={item.imageUrl} alt={item.header} className="h-full w-full object-cover" loading="lazy" />
                        </div>

                        <div className="bg-bg-white h-[calc(96*var(--vh-unit))] rounded-b-[calc(8*var(--vh-unit))] px-[calc(20*var(--vw-unit))] py-[calc(20*var(--vh-unit))] text-center">
                            <div className="text-text-main01 truncate text-[calc(20*var(--vh-unit))] leading-[calc(28*var(--vh-unit))] font-bold">
                                {item.header}
                                <br />
                                {item.description}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
